import { useMemo, useState } from 'react';
import nursingQuestions from './questions.json';
import administrativeQuestions from './preguntas_administrativo.json';
import laboratoryQuestions from './preguntas_laboratorio.json';

const optionKeys = ['a', 'b', 'c', 'd'];

const shuffleQuestions = (list) => {
  const copy = [...list];

  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }

  return copy;
};

const PURGATORY_LIMIT = 1;

function App() {
  const [examType, setExamType] = useState('nursing');
  const [mode, setMode] = useState('random');
  const [quizSeed, setQuizSeed] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedByQuestion, setSelectedByQuestion] = useState({});
  const [jumpValue, setJumpValue] = useState('');
  const [shuffleAnswers, setShuffleAnswers] = useState(true);

  const [purgatory, setPurgatory] = useState(() => {
    try {
      const saved = localStorage.getItem('osakipresas_purgatory');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const [purgatorySessionQuestions, setPurgatorySessionQuestions] = useState([]);

  const getQuestionKey = (q) => {
    if (!q) return '';
    const type = q.examType || examType;
    return `${type}_${q.number}`;
  };

  const questionBank =
    examType === 'nursing'
      ? nursingQuestions
      : examType === 'administrative'
        ? administrativeQuestions
        : laboratoryQuestions;
  const randomQuestions = useMemo(() => shuffleQuestions(questionBank), [quizSeed, questionBank]);
  const activeQuestions =
    mode === 'purgatory'
      ? purgatorySessionQuestions
      : mode === 'random'
        ? randomQuestions
        : questionBank;

  const currentQuestionExamType = mode === 'purgatory' && activeQuestions[currentIndex]
    ? activeQuestions[currentIndex].examType
    : examType;

  const appClassName = `app ${currentQuestionExamType === 'administrative'
      ? 'app--administrative'
      : currentQuestionExamType === 'laboratory'
        ? 'app--laboratory'
        : ''
    } ${mode === 'purgatory' ? 'app--purgatory' : ''}`;

  const examTitles = {
    nursing: 'Test de Enfermería',
    administrative: 'Test de Administrativo',
    laboratory: 'Test de Laboratorio'
  };
  const currentTitle = mode === 'purgatory' ? 'Purgatorio de Preguntas' : examTitles[examType] || 'Test de Enfermería';

  const optionOrderByQuestion = useMemo(() => {
    const orderMap = {};

    activeQuestions.forEach((q) => {
      orderMap[getQuestionKey(q)] =
        (mode === 'random' || mode === 'purgatory') && shuffleAnswers
          ? shuffleQuestions(optionKeys)
          : optionKeys;
    });

    return orderMap;
  }, [activeQuestions, mode, examType, shuffleAnswers]);

  const currentQuestion = activeQuestions[currentIndex];
  const selectedOption = currentQuestion ? selectedByQuestion[getQuestionKey(currentQuestion)] : undefined;
  const isNullQuestion = currentQuestion ? currentQuestion.result === 'nula' : false;
  const isCorrect = currentQuestion ? !isNullQuestion && selectedOption === currentQuestion.result : false;
  const currentOptionOrder = currentQuestion ? optionOrderByQuestion[getQuestionKey(currentQuestion)] || optionKeys : optionKeys;
  const correctShownLetter = currentQuestion && !isNullQuestion
    ? optionKeys[Math.max(currentOptionOrder.indexOf(currentQuestion.result), 0)].toUpperCase()
    : '';

  const score = useMemo(
    () =>
      activeQuestions.reduce(
        (acc, q) => acc + (q.result !== 'nula' && selectedByQuestion[getQuestionKey(q)] === q.result ? 1 : 0),
        0
      ),
    [selectedByQuestion, activeQuestions, examType]
  );

  const answeredQuestions = useMemo(
    () => activeQuestions.filter((q) => Boolean(selectedByQuestion[getQuestionKey(q)])).length,
    [activeQuestions, selectedByQuestion, examType]
  );

  const quizFinished =
    (mode === 'random' || mode === 'purgatory') && currentIndex >= activeQuestions.length && activeQuestions.length > 0;

  const handleSelect = (option) => {
    if (!currentQuestion || selectedOption) {
      return;
    }

    const key = getQuestionKey(currentQuestion);
    setSelectedByQuestion((prev) => ({
      ...prev,
      [key]: option
    }));

    const isNullQuestion = currentQuestion.result === 'nula';
    const isCorrect = !isNullQuestion && option === currentQuestion.result;

    if (mode === 'purgatory') {
      if (isCorrect) {
        setPurgatory((prev) => {
          const next = prev.filter(
            (item) => !(item.examType === currentQuestion.examType && item.number === currentQuestion.number)
          );
          localStorage.setItem('osakipresas_purgatory', JSON.stringify(next));
          return next;
        });
      }
    } else if (mode === 'random') {
      if (!isCorrect && !isNullQuestion) {
        setPurgatory((prev) => {
          const exists = prev.some(
            (item) => item.examType === examType && item.number === currentQuestion.number
          );
          if (exists) return prev;

          const next = [...prev, { examType, number: currentQuestion.number }];
          localStorage.setItem('osakipresas_purgatory', JSON.stringify(next));
          return next;
        });
      }
    }
  };

  const startPurgatoryTest = () => {
    const resolved = purgatory
      .map((item) => {
        const bank =
          item.examType === 'nursing'
            ? nursingQuestions
            : item.examType === 'administrative'
              ? administrativeQuestions
              : laboratoryQuestions;
        const q = bank.find((x) => x.number === item.number);
        return q ? { ...q, examType: item.examType } : null;
      })
      .filter(Boolean);

    setPurgatorySessionQuestions(resolved);
    setMode('purgatory');
    setCurrentIndex(0);
    setSelectedByQuestion({});
  };

  const goNextRandom = () => {
    if (selectedOption && currentIndex < activeQuestions.length) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const goPreviousSequential = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const goNextSequential = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, activeQuestions.length - 1));
  };

  const jumpToQuestion = () => {
    const parsed = Number(jumpValue);

    if (!Number.isInteger(parsed) || parsed < 1 || parsed > activeQuestions.length) {
      return;
    }

    setCurrentIndex(parsed - 1);
    setJumpValue('');
  };

  const resetQuiz = () => {
    setSelectedByQuestion({});
    setCurrentIndex(0);
    setJumpValue('');
    setQuizSeed((prev) => prev + 1);
  };

  const switchExamType = (nextExamType) => {
    setExamType(nextExamType);
    setSelectedByQuestion({});
    setMode('random');
    setCurrentIndex(0);
    setJumpValue('');
    setQuizSeed((prev) => prev + 1);
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setCurrentIndex(0);
    setJumpValue('');

    if (nextMode === 'random') {
      setQuizSeed((prev) => prev + 1);
    }
  };

  if (!activeQuestions.length) {
    return (
      <main className={appClassName}>
        <h1>{currentTitle}</h1>
        <p className="feedback">No hay preguntas disponibles en el JSON o el Purgatorio está vacío.</p>
      </main>
    );
  }

  if (quizFinished) {
    return (
      <main className={appClassName}>
        <header className="app__header">
          <h1>{currentTitle}</h1>
          <div className="mode-switch">
            <button type="button" className={mode === 'random' ? 'secondary' : ''} onClick={() => switchMode('random')}>
              Modo Aleatorio
            </button>
            <button type="button" className={mode === 'sequential' ? 'secondary' : ''} onClick={() => switchMode('sequential')}>
              Modo Secuencial
            </button>
            <button
              type="button"
              className={`purgatory-button ${mode === 'purgatory' ? 'active' : ''}`}
              disabled={purgatory.length < PURGATORY_LIMIT}
              onClick={startPurgatoryTest}
            >
              Purgatorio ({purgatory.length})
            </button>
          </div>
        </header>

        <section className="card">
          <h2>{mode === 'purgatory' ? '¡Test de Purgatorio finalizado!' : '¡Test finalizado!'}</h2>
          <p className="result">
            {mode === 'purgatory' ? (
              <>
                Preguntas superadas (sacadas del purgatorio): {score} / {activeQuestions.length}
                <br />
                Restantes en el purgatorio: {purgatory.length}
              </>
            ) : (
              `Puntuación final: ${score} / ${activeQuestions.length}`
            )}
          </p>
          <button type="button" onClick={mode === 'purgatory' ? () => { switchMode('random'); setSelectedByQuestion({}); } : resetQuiz}>
            {mode === 'purgatory' ? 'Volver al Modo Aleatorio' : 'Volver a empezar (nuevo orden aleatorio)'}
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className={appClassName}>
      <header className="app__header">
        <h1>{currentTitle}</h1>
        <div className="mode-switch">
          <button type="button" className={mode === 'random' ? 'secondary' : ''} onClick={() => switchMode('random')}>
            Modo Aleatorio
          </button>
          <button type="button" className={mode === 'sequential' ? 'secondary' : ''} onClick={() => switchMode('sequential')}>
            Modo Secuencial
          </button>
          <button
            type="button"
            className={`purgatory-button ${mode === 'purgatory' ? 'active' : ''}`}
            disabled={purgatory.length < PURGATORY_LIMIT}
            onClick={startPurgatoryTest}
          >
            Purgatorio ({purgatory.length})
          </button>
        </div>
        <p className="question-progress">
          <span>
            Pregunta {currentIndex + 1} de {activeQuestions.length} (ID original: {currentQuestion.number})
            {mode === 'random' && (
              <label className="shuffle-toggle">
                <input
                  type="checkbox"
                  checked={shuffleAnswers}
                  disabled={Boolean(selectedOption)}
                  onChange={(e) => setShuffleAnswers(e.target.checked)}
                />
                Orden aleatorio de respuestas
              </label>
            )}
          </span>
          {(mode === 'random' || mode === 'purgatory') && (
            <span className="random-score">
              Respuestas correctas: {score}/{answeredQuestions}
            </span>
          )}
        </p>
        {mode === 'sequential' && (
          <div className="sequential-top-nav">
            <button type="button" onClick={goPreviousSequential} disabled={currentIndex === 0}>
              ←
            </button>
            <button
              type="button"
              onClick={goNextSequential}
              disabled={currentIndex === activeQuestions.length - 1}
            >
              →
            </button>
            <input
              type="number"
              min="1"
              max={activeQuestions.length}
              value={jumpValue}
              onChange={(e) => setJumpValue(e.target.value)}
              placeholder="Nº"
            />
            <button type="button" onClick={jumpToQuestion}>
              Ir
            </button>
          </div>
        )}
      </header>

      <section className="quiz">
        <article className="card">
          <h2>{currentQuestion.question}</h2>

          <div className="options" role="radiogroup" aria-label={`Pregunta ${currentQuestion.number}`}>
            {(optionOrderByQuestion[getQuestionKey(currentQuestion)] || optionKeys).map((answerKey, index) => {
              const shownLetter = optionKeys[index].toUpperCase();
              const isPicked = selectedOption === answerKey;
              const correctOption = currentQuestion.result;
              const showCorrect = selectedOption && !isNullQuestion && answerKey === correctOption;
              const showWrong = selectedOption && !isNullQuestion && isPicked && answerKey !== correctOption;

              return (
                <label
                  key={answerKey}
                  className={`option ${isPicked ? 'option--active' : ''} ${showCorrect ? 'option--correct' : ''
                    } ${showWrong ? 'option--wrong' : ''}`}
                >
                  <input
                    type="radio"
                    name={`question-${getQuestionKey(currentQuestion)}`}
                    value={answerKey}
                    checked={isPicked}
                    onChange={() => handleSelect(answerKey)}
                    disabled={Boolean(selectedOption)}
                  />
                  <span className="letter">{shownLetter}.</span>
                  <span>{currentQuestion[answerKey]}</span>
                </label>
              );
            })}
          </div>

          {selectedOption && (
            <p className={`feedback ${isNullQuestion ? 'feedback--null' : isCorrect ? 'feedback--ok' : ''}`}>
              {isNullQuestion
                ? '⚠️ Pregunta anulada: está mal formulada o tiene más de una respuesta correcta.'
                : isCorrect
                  ? '✅ ¡Correcta!'
                  : `❌ Incorrecta. La respuesta correcta es ${correctShownLetter}.`}
            </p>
          )}

          {mode === 'random' || mode === 'purgatory' ? (
            <div className="actions actions--next-sticky">
              <button type="button" className="next-button" onClick={goNextRandom} disabled={!selectedOption}>
                {currentIndex === activeQuestions.length - 1 ? 'Ver resultado final' : 'Siguiente pregunta'}
              </button>
            </div>
          ) : (
            <div className="actions sequential-actions">
              <p className="result">
                Aciertos: {score} / {activeQuestions.length}
              </p>
            </div>
          )}
        </article>
      </section>

      <footer className="exam-switch">
        <p>Tipo de oposición:</p>
        <div className="exam-switch__buttons">
          <button
            type="button"
            className={examType === 'nursing' ? 'secondary' : ''}
            onClick={() => switchExamType('nursing')}
          >
            Enfermera
          </button>
          <button
            type="button"
            className={examType === 'administrative' ? 'secondary' : ''}
            onClick={() => switchExamType('administrative')}
          >
            Administrativo
          </button>
          <button
            type="button"
            className={examType === 'laboratory' ? 'secondary' : ''}
            onClick={() => switchExamType('laboratory')}
          >
            Laboratorio
          </button>
        </div>
      </footer>
    </main>
  );
}

export default App;
